import type { ColDef } from 'ag-grid-community';
import type { Batch, BatchUser } from '../../hooks/useProjectBatches';
import { BatchActionsCellRenderer } from './BatchActionsCellRenderer';

// Pinned totals rows have totalReviewed/totalNotReviewed instead of per-batch numReviewed/numNotReviewed
interface BatchRow extends Batch {
  totalReviewed?: number;
  totalNotReviewed?: number;
}

interface BatchCellRendererParams {
  data?: BatchRow;
  node?: { rowPinned?: string };
  value?: number;
}

function progressBar(reviewed: number, notReviewed: number): string {
  const total = reviewed + notReviewed;
  const pct = total > 0 ? Math.floor((reviewed / total) * 100) : 0;
  return `<div class="progress" style="margin-top:7px"><div class="progress-bar" style="width:${pct}%">${pct}%</div></div>`;
}

interface Options {
  projectId: string;
  canManageBatches: boolean;
}

export function buildBatchColumns({ projectId, canManageBatches }: Options): ColDef[] {
  return [
    {
      headerName: 'Batch',
      colId: 'batchName',
      filter: false,
      sortable: false,
      minWidth: 120,
      width: 170,
      pinnedRowCellRenderer: () => '',
      cellRenderer: (params: BatchCellRendererParams) => {
        if (!params.data) return '';
        return `<a data-testid="batch-list-name-link" href="#!/project/${projectId}/home/batch/${params.data.id}">${params.data.name}</a>`;
      },
    },
    {
      headerName: 'Assigned To',
      colId: 'assignedTo',
      filter: false,
      sortable: false,
      minWidth: 150,
      width: 400,
      hide: !canManageBatches,
      // valueGetter gives ag-Grid a comparable value so it detects add/remove changes
      valueGetter: (params: BatchCellRendererParams) =>
        (params.data?.users || []).map((u: BatchUser) => u.id).join(',') + '|' +
        (params.data?.roles || []).map((r: { name: string }) => r.name).join(','),
      pinnedRowCellRenderer: () => '',
      cellRenderer: (params: BatchCellRendererParams) => {
        if (!params.data || params.node?.rowPinned) return '';
        const users = (params.data.users || [])
          .map((u: BatchUser) => `<span class="badge badge-info"><i class="fas fa-user"></i> ${u.name}</span>`)
          .join(' ');
        const roles = (params.data.roles || [])
          .map((r: { name: string }) => `<span class="badge badge-info"><i class="fas fa-users"></i> ${r.name}</span>`)
          .join(' ');
        return users + roles;
      },
    },
    {
      headerName: 'Total Items',
      field: 'totalItems',
      colId: 'totalItems',
      filter: false,
      sortable: false,
      minWidth: 90,
      width: 130,
      pinnedRowCellRenderer: (params: BatchCellRendererParams) => params.data?.totalItems?.toLocaleString() ?? '',
      valueFormatter: (params: BatchCellRendererParams) => params.value != null ? params.value.toLocaleString() : '',
    },
    {
      headerName: 'Reviewed',
      colId: 'reviewed',
      filter: false,
      sortable: false,
      minWidth: 90,
      width: 130,
      pinnedRowCellRenderer: (params: BatchCellRendererParams) => params.data?.totalReviewed?.toLocaleString() ?? '',
      cellRenderer: (params: BatchCellRendererParams) => {
        if (!params.data) return '';
        return `<a data-testid="batch-list-reviewed-link" href="#!/project/${projectId}/home/batch/${params.data.id}/reviewed">${params.data.numReviewed}</a>`;
      },
    },
    {
      headerName: 'Not Reviewed',
      colId: 'notReviewed',
      filter: false,
      sortable: false,
      minWidth: 90,
      width: 130,
      pinnedRowCellRenderer: (params: BatchCellRendererParams) => params.data?.totalNotReviewed?.toLocaleString() ?? '',
      cellRenderer: (params: BatchCellRendererParams) => {
        if (!params.data) return '';
        return `<a data-testid="batch-list-not-reviewed-link" href="#!/project/${projectId}/home/batch/${params.data.id}/notReviewed">${params.data.numNotReviewed}</a>`;
      },
    },
    {
      headerName: 'Progress',
      colId: 'progress',
      filter: false,
      sortable: false,
      minWidth: 120,
      width: 200,
      pinnedRowCellRenderer: (params: BatchCellRendererParams) => {
        if (!params.data) return '';
        return progressBar(params.data.totalReviewed ?? 0, params.data.totalNotReviewed ?? 0);
      },
      cellRenderer: (params: BatchCellRendererParams) => {
        if (!params.data) return '';
        return progressBar(params.data.numReviewed ?? 0, params.data.numNotReviewed ?? 0);
      },
    },
    {
      headerName: 'Actions',
      colId: 'actions',
      filter: false,
      sortable: false,
      width: 200,
      minWidth: 200,
      hide: !canManageBatches,
      pinnedRowCellRenderer: () => '',
      cellRendererFramework: BatchActionsCellRenderer as unknown as React.ComponentType,
    },
  ];
}
